import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router';
import { PageShell } from './components/layout/PageShell';
import { CookieConsent } from './components/shared/CookieConsent';
import {
  LandingSkeleton,
  SearchResultsSkeleton,
  AuthSkeleton,
  PricingSkeleton,
  ProfileSkeleton,
  LegalPageSkeleton,
  PageSkeleton,
  ChangelogSkeleton,
  DashboardSkeleton,
} from './components/shared/LoadingSkeleton';

// Route-level code splitting with React.lazy()
// Per US-025: Performance Optimization & Caching
const Landing = lazy(() =>
  import('./pages/Landing').then((m) => ({ default: m.Landing }))
);
const SearchResults = lazy(() =>
  import('./pages/SearchResults').then((m) => ({ default: m.SearchResults }))
);
const ActDetails = lazy(() =>
  import('./pages/ActDetails').then((m) => ({ default: m.ActDetails }))
);
const Login = lazy(() =>
  import('./pages/Login').then((m) => ({ default: m.Login }))
);
const Register = lazy(() =>
  import('./pages/Register').then((m) => ({ default: m.Register }))
);
const ForgotPassword = lazy(() =>
  import('./pages/ForgotPassword').then((m) => ({ default: m.ForgotPassword }))
);
const ResetPassword = lazy(() =>
  import('./pages/ResetPassword').then((m) => ({ default: m.ResetPassword }))
);
const Pricing = lazy(() =>
  import('./pages/Pricing').then((m) => ({ default: m.Pricing }))
);
const Profile = lazy(() =>
  import('./pages/Profile').then((m) => ({ default: m.Profile }))
);
const NotFound = lazy(() =>
  import('./pages/NotFound').then((m) => ({ default: m.NotFound }))
);
const PrivacyPolicy = lazy(() =>
  import('./pages/legal/PrivacyPolicy').then((m) => ({ default: m.PrivacyPolicy }))
);
const TermsOfService = lazy(() =>
  import('./pages/legal/TermsOfService').then((m) => ({ default: m.TermsOfService }))
);
const CookiePolicy = lazy(() =>
  import('./pages/legal/CookiePolicy').then((m) => ({ default: m.CookiePolicy }))
);
const Changelog = lazy(() =>
  import('./pages/Changelog').then((m) => ({ default: m.Changelog }))
);
const Dashboard = lazy(() =>
  import('./pages/Dashboard').then((m) => ({ default: m.Dashboard }))
);
const JurisprudenceSearch = lazy(() =>
  import('./pages/JurisprudenceSearch').then((m) => ({ default: m.JurisprudenceSearch }))
);
const JurisprudenceDetail = lazy(() =>
  import('./pages/JurisprudenceDetail').then((m) => ({ default: m.JurisprudenceDetail }))
);
const ApiDocs = lazy(() =>
  import('./pages/ApiDocs').then((m) => ({ default: m.ApiDocs }))
);
const Checklists = lazy(() =>
  import('./pages/Checklists').then((m) => ({ default: m.Checklists }))
);
const ChecklistDetail = lazy(() =>
  import('./pages/ChecklistDetail').then((m) => ({ default: m.ChecklistDetail }))
);
const FiscalCompliance = lazy(() =>
  import('./pages/FiscalCompliance').then((m) => ({ default: m.FiscalCompliance }))
);

function App() {
  return (
    <>
      {/* Cookie consent banner */}
      <CookieConsent />
      <Routes>
        {/* Landing page has its own PageShell */}
        <Route
          path="/"
          element={
            <Suspense fallback={<LandingSkeleton />}>
              <Landing />
            </Suspense>
          }
        />

        {/* Search results page */}
        <Route
          path="/search"
          element={
            <PageShell>
              <Suspense fallback={<SearchResultsSkeleton />}>
                <SearchResults />
              </Suspense>
            </PageShell>
          }
        />

        {/* Act detail page */}
        <Route
          path="/act/:id"
          element={
            <PageShell>
              <Suspense fallback={<PageSkeleton />}>
                <ActDetails />
              </Suspense>
            </PageShell>
          }
        />

        {/* Auth pages */}
        <Route
          path="/login"
          element={
            <PageShell>
              <Suspense fallback={<AuthSkeleton />}>
                <Login />
              </Suspense>
            </PageShell>
          }
        />
        <Route
          path="/register"
          element={
            <PageShell>
              <Suspense fallback={<AuthSkeleton />}>
                <Register />
              </Suspense>
            </PageShell>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PageShell>
              <Suspense fallback={<AuthSkeleton />}>
                <ForgotPassword />
              </Suspense>
            </PageShell>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PageShell>
              <Suspense fallback={<AuthSkeleton />}>
                <ResetPassword />
              </Suspense>
            </PageShell>
          }
        />

        {/* Pricing page */}
        <Route
          path="/pricing"
          element={
            <PageShell>
              <Suspense fallback={<PricingSkeleton />}>
                <Pricing />
              </Suspense>
            </PageShell>
          }
        />

        {/* Legal pages */}
        <Route
          path="/confidentialitate"
          element={
            <PageShell>
              <Suspense fallback={<LegalPageSkeleton />}>
                <PrivacyPolicy />
              </Suspense>
            </PageShell>
          }
        />
        <Route
          path="/termeni"
          element={
            <PageShell>
              <Suspense fallback={<LegalPageSkeleton />}>
                <TermsOfService />
              </Suspense>
            </PageShell>
          }
        />
        <Route
          path="/cookie-uri"
          element={
            <PageShell>
              <Suspense fallback={<LegalPageSkeleton />}>
                <CookiePolicy />
              </Suspense>
            </PageShell>
          }
        />

        {/* Profile page */}
        <Route
          path="/profile"
          element={
            <PageShell>
              <Suspense fallback={<ProfileSkeleton />}>
                <Profile />
              </Suspense>
            </PageShell>
          }
        />

        {/* Changelog / Legislative News page - US-028 */}
        <Route
          path="/noutati-legislative"
          element={
            <PageShell>
              <Suspense fallback={<ChangelogSkeleton />}>
                <Changelog />
              </Suspense>
            </PageShell>
          }
        />

        {/* Dashboard page - US-032 */}
        <Route
          path="/dashboard"
          element={
            <Suspense fallback={<DashboardSkeleton />}>
              <Dashboard />
            </Suspense>
          }
        />

        {/* Jurisprudence pages - US-039 */}
        <Route
          path="/jurisprudenta"
          element={
            <PageShell>
              <Suspense fallback={<PageSkeleton />}>
                <JurisprudenceSearch />
              </Suspense>
            </PageShell>
          }
        />
        <Route
          path="/jurisprudenta/:id"
          element={
            <PageShell>
              <Suspense fallback={<PageSkeleton />}>
                <JurisprudenceDetail />
              </Suspense>
            </PageShell>
          }
        />

        {/* API Documentation - US-040 */}
        <Route
          path="/api-docs"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <ApiDocs />
            </Suspense>
          }
        />

        {/* Checklist pages - US-041 */}
        <Route
          path="/checklist"
          element={
            <PageShell>
              <Suspense fallback={<PageSkeleton />}>
                <Checklists />
              </Suspense>
            </PageShell>
          }
        />
        <Route
          path="/checklist/:id"
          element={
            <PageShell>
              <Suspense fallback={<PageSkeleton />}>
                <ChecklistDetail />
              </Suspense>
            </PageShell>
          }
        />

        {/* Fiscal Compliance page - US-042 */}
        <Route
          path="/conformitate-fiscala"
          element={
            <PageShell>
              <Suspense fallback={<PageSkeleton />}>
                <FiscalCompliance />
              </Suspense>
            </PageShell>
          }
        />

        {/* 404 Not Found */}
        <Route
          path="*"
          element={
            <PageShell>
              <Suspense fallback={<PageSkeleton />}>
                <NotFound />
              </Suspense>
            </PageShell>
          }
        />
      </Routes>
    </>
  );
}

export default App;
